import { useEffect, useMemo, useRef, useState } from 'react';

import { useFormat, useScreenSize } from '../../../../../hooks/index';
import {
  VestingGraphData,
  VestingGraphPointStatus,
} from '../../../../../hooks/vesting-task/use-vesting-graph';
import { SharePlanVestingType } from '../../../../../types/pool-plans.types';
import {
  VESTING_GRAPH_AXIS_TICK_WIDTH_PX,
  VESTING_GRAPH_EVENT_COLUMN_WIDTH_PX,
  VESTING_GRAPH_HEIGHT_PX,
  VESTING_GRAPH_MIN_BAR_SIZE_PX,
  VESTING_GRAPH_NARROW_SCREEN_PX,
  VESTING_GRAPH_PLOT_BOTTOM_INSET_PX,
  VESTING_GRAPH_PLOT_RIGHT_INSET_PX,
  VESTING_GRAPH_Y_AXIS_WIDTH,
  VESTING_GRAPH_Y_DOMAIN_FLOOR,
} from '../model/vesting-graph.constants';
import {
  resolveVestingGraphAreaIntroMs,
  resolveVestingGraphBarIntroDelayMs,
  resolveVestingGraphRevealDelayMs,
} from '../model/vesting-graph.intro';
import {
  applyFixedPixelColumnWidths,
  buildCliffOverlay,
  buildMilestoneBrackets,
  buildUnvestedSummary,
  buildVestingGraphAxisTicks,
  buildVestingGraphChartData,
  findAccelerationColumnIndex,
  findExpiryColumnIndex,
  findNextVestIndex,
  findTimestampIndex,
  getTimestampPosition,
  resolveHoveredVestingPeriod,
  resolveOverlappingEventMarkers,
} from '../model/vesting-graph.utils';
import { vestingGraphVariantByGranularity } from '../model/vesting-graph.variants';
import { useVestingGraphHoverGate } from './use-vesting-graph-hover-gate';
import { useVestingGraphIntro } from './use-vesting-graph-intro';
import { resolveAllocationBarWidthPx } from './vesting-graph-bar';
import { ChartTooltipPortalState } from './vesting-graph-tooltip-sync';

export const useVestingGraphView = (
  vestingGraph: VestingGraphData,
  { allowHover = true }: { allowHover?: boolean } = {},
) => {
  const { format } = useFormat();
  const { width } = useScreenSize();
  const [chartPointer, setChartPointer] = useState<{ x: number; y: number } | null>(null);
  const [chartTooltip, setChartTooltip] = useState<ChartTooltipPortalState | null>(null);
  const [measuredChartWidth, setMeasuredChartWidth] = useState<null | number>(null);
  const chartShellRef = useRef<HTMLDivElement>(null);

  const variant = vestingGraphVariantByGranularity[vestingGraph.granularity];

  const builtChart = useMemo(() => {
    const {
      chartData: data,
      domainMax: max,
      domainMin: min,
      milestoneSegmentBounds: segmentBounds,
    } = buildVestingGraphChartData({
      format,
      vestingGraph,
    });

    return {
      chartData: data,
      chartMinWidth: variant.resolveChartMinWidth({
        isNarrowScreen: width < VESTING_GRAPH_NARROW_SCREEN_PX,
        // Milestone charts size by target count, not the expanded linear segment points.
        pointCount: variant.showMilestoneMarkers ? vestingGraph.points.length : data.length,
      }),
      domainMax: max,
      domainMin: min,
      milestoneSegmentBounds: segmentBounds,
      unvestedSummary: buildUnvestedSummary(vestingGraph),
    };
  }, [format, variant, vestingGraph, width]);

  // Prefer the measured SVG width so bars/bands track the stretched chart on wide
  // viewports; fall back to minWidth before the first ResizeObserver tick.
  const plotWidth = Math.max(
    (measuredChartWidth ?? builtChart.chartMinWidth) -
      VESTING_GRAPH_Y_AXIS_WIDTH -
      VESTING_GRAPH_PLOT_RIGHT_INSET_PX,
    1,
  );

  const {
    chartData,
    chartMinWidth,
    cliffOverlay,
    domainMax,
    domainMin,
    milestoneSegmentBounds,
    unvestedSummary,
  } = useMemo(() => {
    const laidOut = variant.showValueBars
      ? {
          ...builtChart,
          ...applyFixedPixelColumnWidths({
            chartData: builtChart.chartData,
            domainMax: builtChart.domainMax,
            domainMin: builtChart.domainMin,
            isTargetColumn: (point) => Boolean(point.isAccelerationColumn || point.isExpiryColumn),
            plotWidth,
            widthPx: VESTING_GRAPH_EVENT_COLUMN_WIDTH_PX,
          }),
        }
      : builtChart;

    return {
      ...laidOut,
      cliffOverlay: buildCliffOverlay({
        chartPoints: laidOut.chartData,
        domainMax: laidOut.domainMax,
        domainMin: laidOut.domainMin,
        pointCount: laidOut.chartData.length,
        vestingGraph,
      }),
    };
  }, [builtChart, plotWidth, variant.showValueBars, vestingGraph]);

  const milestoneBrackets = useMemo(
    () =>
      variant.showMilestoneMarkers
        ? buildMilestoneBrackets({
            domainMax,
            domainMin,
            segmentBounds: milestoneSegmentBounds,
            vestingGraph,
          })
        : [],
    [domainMax, domainMin, milestoneSegmentBounds, variant, vestingGraph],
  );

  const accelerationIndex =
    findAccelerationColumnIndex(chartData) ??
    findTimestampIndex(chartData, vestingGraph.accelerationDate);
  const expiryIndex =
    findExpiryColumnIndex(chartData) ?? findTimestampIndex(chartData, vestingGraph.expiryDate);
  const accelerationTimestamp = vestingGraph.accelerationDate
    ? new Date(vestingGraph.accelerationDate).getTime()
    : null;
  const asOfTimestamp = new Date(vestingGraph.asOfDate).getTime();
  // Only a passed acceleration freezes the later columns. A future accel date must not
  // swallow the dashed “current” column on Dynamic the way it used to.
  const accelerationHasPassed =
    accelerationTimestamp !== null && accelerationTimestamp <= asOfTimestamp;
  const nextVestIndex = findNextVestIndex(chartData, {
    beforeTimestamp: accelerationHasPassed ? accelerationTimestamp : null,
  });

  useEffect(() => {
    const shell = chartShellRef.current;
    if (!shell) return undefined;

    const syncWidth = () => {
      setMeasuredChartWidth(shell.clientWidth);
    };

    syncWidth();
    const resizeObserver = new ResizeObserver(syncWidth);
    resizeObserver.observe(shell);

    return () => resizeObserver.disconnect();
  }, [chartData, chartMinWidth]);

  const introPhase = useVestingGraphIntro({
    layoutReady: measuredChartWidth !== null,
    pointCount: chartData.length,
  });
  const { isHoverLive, isPlotInteractive, onPlotPointerLeave, onPlotPointerMove } =
    useVestingGraphHoverGate({ allowHover, introPhase });

  useEffect(() => {
    if (isHoverLive) return;

    setChartPointer(null);
    setChartTooltip(null);
  }, [isHoverLive]);

  const introAreaDurationMs = resolveVestingGraphAreaIntroMs(chartData.length);
  const introDelayForBar = (index: number) =>
    resolveVestingGraphBarIntroDelayMs(index, chartData.length);
  const introDelayForReveal = (leftPercent: number) =>
    resolveVestingGraphRevealDelayMs(leftPercent, chartData.length);

  const axisTicks = buildVestingGraphAxisTicks({
    chartPoints: chartData,
    domainMax,
    domainMin,
    minGapPercent: (VESTING_GRAPH_AXIS_TICK_WIDTH_PX / plotWidth) * 100,
    segmentBounds: milestoneSegmentBounds,
    vestingGraph,
  });
  // Payment columns share one layout unit. Recharts still needs one barSize — use the
  // widest drawn bar so hit-targets cover every column; the shape recentres each bar.
  const domainSpan = Math.max(domainMax - domainMin, 1);
  const barSize = Math.max(
    VESTING_GRAPH_MIN_BAR_SIZE_PX,
    ...chartData.map((point) =>
      resolveAllocationBarWidthPx({
        domainSpan,
        plotWidth,
        slotHalfWidth: point.slotHalfWidth,
      }),
    ),
  );
  const plotOverlayHeight =
    VESTING_GRAPH_HEIGHT_PX - variant.topMargin - VESTING_GRAPH_PLOT_BOTTOM_INSET_PX;
  // Same lift the negative Y-domain floor gives the bars above the axis rule.
  const barBaselineLiftPx =
    (Math.abs(VESTING_GRAPH_Y_DOMAIN_FLOOR) / (100 - VESTING_GRAPH_Y_DOMAIN_FLOOR)) *
    plotOverlayHeight;
  const hasOriginalSchedule = Boolean(vestingGraph.originalSchedulePoints?.length);
  // Same hover as the tooltip: Recharts reports the column from anywhere in the plot,
  // while Bar mouseEnter only fires on the fill path — so the period plaque used to
  // flicker on and off for the same bar.
  const hoveredPoint = isHoverLive ? (chartTooltip?.payload[0]?.payload ?? null) : null;
  const hoveredPeriod =
    isHoverLive && vestingGraph.vestingType === SharePlanVestingType.TIME_BASED_DYNAMIC
      ? resolveHoveredVestingPeriod({
          chartPoints: chartData,
          domainMax,
          domainMin,
          timestamp: hoveredPoint?.calendarTimestamp ?? hoveredPoint?.timestamp ?? null,
          vestingPeriods: vestingGraph.vestingPeriods,
        })
      : null;

  const positionAt = (index: number) =>
    getTimestampPosition({
      maxTimestamp: domainMax,
      minTimestamp: domainMin,
      timestamp: chartData[index]!.timestamp,
    });
  const accelerationLeftPercent = accelerationIndex === null ? null : positionAt(accelerationIndex);
  const expiryLeftPercent = expiryIndex === null ? null : positionAt(expiryIndex);
  const { accelerationStack, expiryStack } = resolveOverlappingEventMarkers({
    accelerationLeftPercent,
    expiryLeftPercent,
    minGapPercent: (VESTING_GRAPH_AXIS_TICK_WIDTH_PX / plotWidth) * 100,
  });

  const plotOverlayStyle = {
    height: plotOverlayHeight,
    left: VESTING_GRAPH_Y_AXIS_WIDTH,
    right: VESTING_GRAPH_PLOT_RIGHT_INSET_PX,
    top: variant.topMargin,
  };

  const vestedShares = vestingGraph.points
    .filter((point) => point.status === VestingGraphPointStatus.VESTED)
    .reduce((sum, point) => sum + point.periodShares, 0);
  const totalShares = vestingGraph.points.reduce((sum, point) => sum + point.periodShares, 0);

  return {
    accelerationIndex,
    accelerationStack,
    axisTicks,
    barBaselineLiftPx,
    barSize,
    chartData,
    chartMinWidth,
    chartPointer,
    chartShellRef,
    chartTooltip,
    cliffOverlay,
    domainMax,
    domainMin,
    domainSpan,
    expiryIndex,
    expiryStack,
    format,
    hasOriginalSchedule,
    hoveredPeriod,
    hoveredPoint,
    introAreaDurationMs,
    introDelayForBar,
    introDelayForReveal,
    introPhase,
    isHoverLive,
    isPlotInteractive,
    nextVestIndex,
    onPlotPointerLeave,
    onPlotPointerMove,
    plotOverlayStyle,
    plotWidth,
    positionAt,
    setChartPointer,
    setChartTooltip,
    totalShares,
    unvestedSummary,
    variant,
    vestedShares,
    milestoneBrackets,
  };
};

export type VestingGraphView = ReturnType<typeof useVestingGraphView>;
